/**
 * Subscription Modal - Western-themed upgrade prompt for Pro features.
 * Maintains exact design consistency with wanted-poster aesthetic.
 */

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Star, X, Loader2, Lock, ShieldCheck, BadgeCheck, Gift } from "lucide-react";
import { PRO_CONFIG, BOUNTY_CONFIG, ROUTES, INTRO_OFFER_ENABLED, INTRO_OFFER_CONFIG, INTRO_OFFER_TERMS } from "@/config/constants";
import { useNavigate } from "react-router-dom";
import { buildAuthUrl } from "@/lib/authFlow";
import { proApi } from "@/services/api";
import { useLocalization } from "@/hooks/useLocalization";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFeature?: string;
}

export const SubscriptionModal = ({ open, onOpenChange, selectedFeature }: SubscriptionModalProps) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { formatCurrency } = useLocalization();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [loading, setLoading] = useState(false);

  const currentTier = billingCycle === "monthly" ? PRO_CONFIG.TIERS.MONTHLY : PRO_CONFIG.TIERS.YEARLY;

  const features = [
    PRO_CONFIG.FEATURES.LOWER_FEES,
    PRO_CONFIG.FEATURES.CUSTOM_REWARDS,
    PRO_CONFIG.FEATURES.EXTENDED_DURATION,
    PRO_CONFIG.FEATURES.PRIORITY_LISTING,
    PRO_CONFIG.FEATURES.ANALYTICS,
    PRO_CONFIG.FEATURES.BULK_POSTING,
    PRO_CONFIG.FEATURES.CUSTOM_PROOF,
    PRO_CONFIG.FEATURES.VERIFICATION_BADGE,
    PRO_CONFIG.FEATURES.DIRECT_MESSAGING,
    PRO_CONFIG.FEATURES.PRIORITY_SUPPORT,
  ];

  const handleUpgrade = async () => {
    if (!user) {
      onOpenChange(false);
      nav(buildAuthUrl(ROUTES.SUBSCRIPTION));
      return;
    }

    // Refresh auth token before making payment
    try {
      const { data: { session: freshSession }, error } = await supabase.auth.refreshSession();
      if (error || !freshSession) {
        toast.error("Session expired. Please log in again.");
        await supabase.auth.signOut();
        onOpenChange(false);
        return;
      }
    } catch (error) {
      toast.error("Session expired. Please log in again.");
      await supabase.auth.signOut();
      onOpenChange(false);
      return;
    }

    setLoading(true);
    try {
      // Try Stripe Checkout first (production payment flow)
      toast.message("Redirecting to secure checkout...", { description: "Powered by Stripe" });

      const { data, error } = await proApi.createSubscriptionCheckout(
        user.id,
        currentTier.id as "monthly" | "yearly",
        currentTier.price_cents,
        user.email || ""
      );

      if (error) {
        throw error;
      }

      // Redirect to Stripe Checkout
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw_-_1.5rem)] flex-col gap-0 overflow-hidden rounded-2xl border-2 border-[hsl(var(--paper-edge))] bg-[hsl(var(--paper))] p-0 sm:w-full sm:max-w-3xl sm:max-h-[88vh] [&>button]:z-20 [&>button]:text-[hsl(25_50%_12%)] [&>button]:opacity-60 [&>button]:hover:opacity-100">
        {/* Hero header */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[hsl(45_85%_55%)] via-[hsl(40_80%_50%)] to-[hsl(35_75%_42%)] px-5 py-6 sm:px-8 sm:py-7">
          <Star
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 text-[hsl(25_50%_12%)] opacity-10"
            fill="currentColor"
            strokeWidth={0.5}
          />
          <div className="relative flex items-start gap-4">
            <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[hsl(25_50%_12%)] shadow-lg sm:flex">
              <Star className="h-7 w-7 text-[hsl(45_85%_55%)] fill-current" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader className="space-y-0 text-left">
                <DialogTitle className="font-display text-2xl font-bold tracking-tight text-[hsl(25_50%_12%)] sm:text-3xl">
                  Get Your Sheriff's Badge
                </DialogTitle>
                <DialogDescription className="mt-1.5 text-sm text-[hsl(25_40%_28%)]">
                  Lower service fees and longer listings. Rewards and fees still apply.
                </DialogDescription>
              </DialogHeader>
              <div className="mt-3.5 flex flex-wrap items-center gap-2 pr-8 sm:pr-0">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--paper))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(25_50%_12%)] shadow-sm dark:text-[hsl(45_85%_55%)]">
                  <ShieldCheck className="h-3 w-3" />
                  Secure Stripe checkout
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--paper))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(25_50%_12%)] shadow-sm dark:text-[hsl(45_85%_55%)]">
                  <Check className="h-3 w-3" />
                  One-time payment
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--paper))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(25_50%_12%)] shadow-sm dark:text-[hsl(45_85%_55%)]">
                  <Star className="h-3 w-3 fill-current" />
                  {features.length} Pro features
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {selectedFeature && (
            <div className="flex items-center justify-center gap-2 border-b border-[hsl(var(--paper-edge))] bg-accent/10 px-5 py-2.5 text-sm font-semibold sm:px-8">
              <Lock className="h-3.5 w-3.5 shrink-0 text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]" />
              <span>
                <span className="text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]">{selectedFeature}</span> is a Pro feature
              </span>
            </div>
          )}

          <div className="space-y-6 p-5 sm:p-8">
            {/* Billing Cycle Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex items-center rounded-full border-2 border-[hsl(var(--paper-edge))] bg-paper/60 p-1 shadow-sm dark:bg-[hsl(25_25%_14%)]">
                <button
                  type="button"
                  onClick={() => setBillingCycle("monthly")}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-all sm:px-5 ${
                    billingCycle === "monthly"
                      ? "bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] text-[hsl(25_50%_12%)] shadow-md"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("yearly")}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-all sm:px-5 ${
                    billingCycle === "yearly"
                      ? "bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] text-[hsl(25_50%_12%)] shadow-md"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Yearly
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      billingCycle === "yearly"
                        ? "bg-[hsl(25_50%_12%)] text-[hsl(45_85%_55%)]"
                        : "bg-success/15 text-success"
                    }`}
                  >
                    Save 33%
                  </span>
                </button>
              </div>
            </div>

            {/* Pricing + Features grid */}
            <div className="grid gap-4 lg:grid-cols-2 lg:gap-5">
              {/* Price card */}
              <div className="poster relative flex flex-col overflow-hidden p-5 sm:p-6">
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-bl-full bg-gradient-to-bl from-accent/20 to-transparent" />
                <div className="relative flex flex-1 flex-col">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[hsl(25_50%_12%)]">
                      <Star className="h-3 w-3 fill-current" />
                      Pro
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">Sheriff's Badge</span>
                  </div>

                  <div className="flex items-baseline gap-2">
                    <div className="font-display text-5xl font-bold text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]">
                      {formatCurrency(currentTier.price_cents)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      / {billingCycle === "monthly" ? "month" : "year"}
                    </div>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {billingCycle === "yearly"
                      ? "Billed once for a full year."
                      : "Save 33% with yearly billing."}
                  </p>

                  <div className="mt-5 space-y-2.5 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-success" />
                      Payments handled securely by Stripe
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 shrink-0 text-success" />
                      One-time purchase, no automatic renewal
                    </div>
                  </div>

                  <div className="mt-auto pt-5">
                    <Button
                      className="shine-sweep w-full bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] font-bold text-[hsl(25_50%_12%)] shadow-md shadow-accent/30 hover:opacity-90"
                      size="lg"
                      onClick={handleUpgrade}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Upgrading...
                        </>
                      ) : (
                        <>
                          <Star className="h-4 w-4 fill-current" />
                          Upgrade to Pro
                        </>
                      )}
                    </Button>

                    <p className="mt-3 text-center text-[11px] text-muted-foreground">
                      You'll be redirected to Stripe to complete payment.
                    </p>
                  </div>
                </div>
              </div>

              {/* Features card */}
              <div className="poster p-5 sm:p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-accent/25 bg-accent/10">
                    <BadgeCheck className="h-4 w-4 text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]" />
                  </span>
                  <h3 className="font-display text-lg font-bold">Everything you unlock</h3>
                </div>
                <ul className="space-y-2.5">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-success/25 bg-success/15">
                        <Check className="h-3 w-3 text-success" />
                      </span>
                      <span className="text-sm leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Free vs Pro Comparison */}
            <div>
              <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                Free vs Pro
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border-2 border-[hsl(var(--paper-edge))] bg-paper/60 p-4 dark:bg-[hsl(25_25%_14%)]">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="font-bold text-muted-foreground">Free</span>
                    <span className="rounded-full border border-[hsl(var(--paper-edge))] bg-muted/60 px-2.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                      $0
                    </span>
                  </div>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{formatCurrency(BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.FREE_MAX_REWARD_CENTS)} reward per hunter</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>7-day default and maximum duration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>20% service fee, {formatCurrency(BOUNTY_CONFIG.MIN_PLATFORM_FEE_CENTS)} minimum</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>1–10 funded hunters per listing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Standard listing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>Basic features</span>
                    </li>
                  </ul>
                </div>

                <div className="relative overflow-hidden rounded-xl border-2 border-accent/50 bg-paper/60 p-4 shadow-md dark:bg-[hsl(25_25%_14%)]">
                  <Star
                    aria-hidden
                    className="pointer-events-none absolute -bottom-6 -right-6 h-24 w-24 text-[hsl(45_85%_55%)] opacity-[0.08]"
                    fill="currentColor"
                    strokeWidth={0.5}
                  />
                  <div className="relative">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 font-bold text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        Pro
                      </span>
                      <span className="rounded-full bg-gradient-to-r from-[hsl(45_85%_55%)] to-[hsl(35_75%_42%)] px-2.5 py-0.5 text-[10px] font-bold text-[hsl(25_50%_12%)]">
                        Recommended
                      </span>
                    </div>
                    <ul className="space-y-2 text-xs">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>{formatCurrency(BOUNTY_CONFIG.PRO_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.PRO_MAX_REWARD_CENTS)} reward per hunter</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>7-day default, up to 30 days</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>10% service fee, {formatCurrency(BOUNTY_CONFIG.PRO_MIN_PLATFORM_FEE_CENTS)} minimum</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>1–10 funded hunters per listing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>Priority listing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                        <span>All premium features</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms + intro offer */}
            <div className="space-y-3 border-t border-[hsl(var(--paper-edge))] pt-4">
              <p className="text-xs text-muted-foreground">
                You fund rewards plus service fees, applicable processing charges, and selected extras. Free has no subscription charge.
              </p>
              {INTRO_OFFER_ENABLED && (
                <details className="rounded-xl border border-accent/25 bg-accent/10 p-3 text-sm">
                  <summary className="flex cursor-pointer items-center gap-2 font-semibold">
                    <Gift className="h-4 w-4 shrink-0 text-[hsl(35_75%_42%)] dark:text-[hsl(45_85%_55%)]" />
                    New launch accounts: {INTRO_OFFER_CONFIG.credits} service-fee credits worth up to {formatCurrency(INTRO_OFFER_CONFIG.maxDiscountCents)} each
                  </summary>
                  <p className="mt-2 text-muted-foreground">{INTRO_OFFER_TERMS}</p>
                </details>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * UpgradePrompt - Inline prompt component for non-modal usage
 */
interface UpgradePromptProps {
  feature: string;
  onUpgrade: () => void;
  compact?: boolean;
}

export const UpgradePrompt = ({ feature, onUpgrade, compact = false }: UpgradePromptProps) => {
  if (compact) {
    return (
      <button
        onClick={onUpgrade}
        className="inline-flex items-center gap-1 text-xs text-[hsl(45_85%_55%)] hover:underline font-bold"
      >
        <Star className="h-3 w-3" />
        Pro Feature
      </button>
    );
  }

  return (
    <div className="poster p-4 text-center">
      <Star className="h-8 w-8 mx-auto mb-2 text-[hsl(45_85%_55%)]" />
      <p className="font-bold mb-2">{feature}</p>
      <p className="text-sm text-muted-foreground mb-3">Upgrade to Pro to unlock this feature</p>
      <Button onClick={onUpgrade} size="sm">
        <Star className="h-4 w-4 mr-2" />
        Upgrade to Pro
      </Button>
    </div>
  );
};
